// Owl Hour History — recent results matching prototype layout.
// Rows: date | tier-pill + description | pip indicators | won/total | result

import type { OwlUserSlip } from "../../owl/useOwlHourUserSlips.ts";
import { OWL_TIER_META, type OwlTier } from "../../owl/useOwlHourSlips.ts";
import { BankToGoalCta } from "../../components/sika-extras/BankToGoalCta.tsx";

// ─── Helpers ──────────────────────────────────────────────────

function fmtDate(raw: string): string {
  // AC-3.3 — no bare dashes on Owl Hour screen.
  if (raw.length === 0) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const day = d.getUTCDate().toString().padStart(2, "0");
  const mon = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  return `${day} ${mon}`;
}

function fmtCurrency(n: number): string {
  // AC-3.3 — render "GH₵0" rather than a bare dash.
  if (n === 0) return "GH₵0";
  const prefix = n > 0 ? "+" : "−";
  return `${prefix}GH₵${Math.abs(n).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ─── Component ────────────────────────────────────────────────

export interface OwlHistoryProps {
  userSlips: readonly OwlUserSlip[];
}

export function OwlHistory({ userSlips }: OwlHistoryProps): JSX.Element {
  if (userSlips.length === 0) {
    return (
      <div className="owl-history">
        <div className="h">Recent owl nights</div>
        <div style={{
          padding: "40px 18px",
          textAlign: "center",
          color: "var(--owl-text-3)",
          fontSize: 13,
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🌙</div>
          Your Owl Hour results will appear here after your first night.
        </div>
      </div>
    );
  }

  return (
    <div className="owl-history">
      <div className="h">
        Recent owl nights
        <a href="#" className="link">View 30-day record →</a>
      </div>

      {userSlips.map((us) => {
        const tierMeta = OWL_TIER_META[us.tier as OwlTier];
        const legsTotal = us.legs_total ?? 0;
        const legsCorrect = us.legs_correct ?? 0;
        const busted = legsTotal - legsCorrect;
        const resultKey = us.result ?? "void";
        const isWin = resultKey === "win";
        const returnVal = isWin && us.actual_return !== null
          ? us.actual_return
          : us.stake ? -us.stake : 0;

        return (
          <div key={us.id} className="owl-hist-row">
            <div className="date">
              <span className="lbl">{fmtDate(us.created_at)}</span>
              {new Intl.DateTimeFormat("en-GH",{timeZone:"Africa/Accra",hour:"2-digit",minute:"2-digit"}).format(new Date(us.created_at))} Accra
            </div>
            <div className="desc">
              <span className="tier-pill">
                {tierMeta?.label ?? us.tier}
              </span>
              &nbsp; {legsCorrect}/{legsTotal} legs
            </div>
            <div className="pips">
              {Array.from({ length: legsTotal }).map((_, idx) => (
                <span
                  key={idx}
                  className={`pip${idx >= legsCorrect ? " busted" : ""}`}
                />
              ))}
            </div>
            <div style={{
              fontSize: 11,
              color: "var(--owl-text-3)",
              fontFamily: "var(--font-mono)",
            }}>
              {legsCorrect}/{legsTotal}
            </div>
            <div className={`result ${isWin ? "pos" : "neg"}`}>
              {fmtCurrency(returnVal)}
            </div>
            {isWin && us.actual_return !== null && us.actual_return > 0 ? (
              <BankToGoalCta
                source="owl_hour"
                sourceId={us.id}
                amount={us.actual_return}
                sourceLabel={`Owl Hour ${tierMeta?.label ?? us.tier}`}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
